import {
  Box,
  TLDefaultFillStyle,
  TLDefaultHorizontalAlignStyle,
  TLDefaultVerticalAlignStyle,
  TLShapeId,
} from "@tldraw/editor";
import React, { useEffect, useState } from "react";
import { TextArea } from "./TextArea";
import { TextHelpers } from "./TextHelper";
import { isLegacyAlign, useEditableText } from "./text-helper";
import { mixColors } from "../CustomCloudUtil";
import { shadowToCssTextShadow } from "../shadowUtils";

// Function to check if font is loaded
const isFontLoaded = (fontFamily: string): boolean => {
  // Remove existing quotes and normalize the font name
  const normalizedFont = fontFamily.replace(/['"]/g, "");
  return document.fonts.check(`16px "${normalizedFont}"`);
};

export interface TextLabelProps {
  shapeId: TLShapeId;
  type: string;
  font: string;
  fontWeight?: number;
  underline?: string;
  italic?: string;
  lineThrough?: string;
  fontSize: number;
  fill?: TLDefaultFillStyle;
  align: TLDefaultHorizontalAlignStyle;
  verticalAlign: TLDefaultVerticalAlignStyle;
  wrap?: boolean;
  text: string;
  labelColor: string;
  bounds?: Box;
  isNote?: boolean;
  isSelected: boolean;
  onKeyDown?(e: React.KeyboardEvent<HTMLTextAreaElement>): void;
  classNamePrefix?: string;
  style?: React.CSSProperties;
  textWidth: number;
  textHeight?: number;
  padding?: number;
  editor?: any;
  transparency: number;
  onClick: any;
  textJustify?: string;
  strokeWidth: number;
  outlineColor?: string;
  isHovered: boolean;
  lineHeight: number;
  letterSpacing: number;
  isEditingAnything: boolean;
  borderWidth: number;
  borderColor: string;
  borderSolid: string;
  borderTopStyle?: string;
  borderBottomStyle?: string;
  borderLeftStyle?: string;
  borderRightStyle?: string;
  isShowBorderTop: boolean;
  isShowBorderBottom: boolean;
  isShowBorderLeft: boolean;
  isShowBorderRight: boolean;
  rounded?: number;
  filterBlur?: number;
  isResizing: boolean;
  flipH?: boolean;
  flipV?: boolean;
  isCreatedFilterBlur?: boolean;
  filterBlurValueWhenHidden?: number;
  outShadowArray?: any;
  inShadowArray?: any;
  colorMix?: any;
  calculatedFill?: string;
  isHiddenBorder?: boolean;
  isHiddenLayerBlur?: boolean;
  outlineColorMix?: any;
  fixedHeight?: boolean;
}

export const TextLabel = React.memo(function TextLabel({
  shapeId,
  type,
  text,
  font,
  underline,
  italic,
  lineThrough,
  fontWeight,
  fontSize,
  align,
  verticalAlign,
  wrap,
  isSelected,
  padding = 0,
  onKeyDown: handleKeyDownCustom,
  classNamePrefix,
  style,
  textWidth,
  textHeight,
  editor,
  transparency,
  onClick,
  textJustify,
  lineHeight,
  letterSpacing,
  isEditingAnything,
  rounded,
  filterBlur,
  strokeWidth,
  flipH,
  flipV,
  outShadowArray = [],
  colorMix,
  isHiddenBorder,
  isHiddenLayerBlur,
  outlineColorMix,
  fixedHeight,
}: TextLabelProps) {
  const { rInput, isEmpty, isEditing, ...editableTextRest } = useEditableText(shapeId, type, text, editor, isSelected);

  const [initialText, setInitialText] = useState(text);
  const [fontLoaded, setFontLoaded] = useState(isFontLoaded(font));

  useEffect(() => {
    if (!isEditingAnything) setInitialText(text);
  }, [isEditingAnything, text]);

  // Check font loading on font change
  useEffect(() => {
    const checkAndLoadFont = async () => {
      if (isFontLoaded(font)) {
        setFontLoaded(true);
      } else {
        setFontLoaded(false);
        try {
          // Remove existing quotes and normalize the font name
          const normalizedFont = font.replace(/['"]/g, "");
          // Load font with the actual size being used, not just 16px
          await document.fonts.load(`${fontSize}px "${normalizedFont}"`);
          // Also load common sizes to ensure font is fully available
          await document.fonts.load(`16px "${normalizedFont}"`);
          // Force re-render after font loads with a longer delay to ensure it's applied
          setTimeout(() => setFontLoaded(true), 150);
        } catch (error) {
          // If font fails to load, still render with fallback
          setFontLoaded(true);
        }
      }
    };

    checkAndLoadFont();
  }, [font, fontSize]);

  const finalText = TextHelpers.normalizeTextForDom(text);
  const hasText = finalText.length > 0;

  const legacyAlign = isLegacyAlign(align);

  if (!isEditingAnything && !hasText) {
    return null;
  }

  // TODO: probably combine tl-text and tl-arrow eventually
  const cssPrefix = classNamePrefix || "tl-text";

  const textShadows = shadowToCssTextShadow(
    Array.isArray(outShadowArray)
      ? Object.fromEntries((outShadowArray as any[]).map((s) => [s.id, s]))
      : (outShadowArray as any)
  );

  const visibleColors = Object.values(colorMix as any)
    .filter((colorObj: any) => !colorObj.isHidden)
    .map((colorObj: any) => colorObj.color);

  const mixedColor = visibleColors.length > 0 ? mixColors(visibleColors) : "transparent";

  const mixedOutlineColor = mixColors(
    Object.values(outlineColorMix as any)
      .filter((colorObj: any) => !colorObj.isHidden)
      .map((colorObj: any) => colorObj.color)
  );

  // Use the font only if it's loaded, otherwise use fallback
  const effectiveFontFamily = `"${font}", -apple-system, BlinkMacSystemFont, sans-serif`;

  // Map verticalAlign to CSS alignItems
  const getAlignItems = () => {
    switch (verticalAlign) {
      case "start":
        return "flex-start";
      case "end":
        return "flex-end";
      case "middle":
      default:
        return "center";
    }
  };

  // const textInShadow = shadowArray
  //   .filter((shadow: any) => !shadow.isHidden)
  //   .map((shadow: any) => `${shadow.dx}px ${shadow.dy}px ${shadow.shadowBlur}px ${hexToRgba(shadow.color)}`)
  //   .join(", ");

  const justifyContentValue = getAlignItems();

  return (
    <div
      key={`${font}-${fontLoaded}`}
      onClick={onClick}
      className={`${cssPrefix}-label tl-text-wrapper`}
      data-font={`${font}`}
      data-align={align}
      data-hastext={!isEmpty}
      data-isediting={isEditingAnything}
      data-iseditinganything={isEditingAnything}
      data-textwrap={!!wrap}
      data-isselected={isSelected}
      style={{
        fontFamily: effectiveFontFamily,
        borderRadius: rounded,
        fontWeight,
        display: "flex",
        flexDirection: "column",
        justifyContent: justifyContentValue, // Vertical alignment (in flexDirection column, justifyContent controls vertical)
        height: "100%",
        width: "100%",
        padding,
        ...style,
        textShadow: textShadows,
        // borderTop: isShowBorderTop ? `${borderWidth}px ${borderSolid ?? "solid"} ${borderColor}` : "none",
        // borderBottom: isShowBorderBottom ? `${borderWidth}px ${borderSolid ?? "solid"} ${borderColor}` : "none",
        // borderLeft: isShowBorderLeft ? `${borderWidth}px ${borderSolid ?? "solid"} ${borderColor}` : "none",
        // borderRight: isShowBorderRight ? `${borderWidth}px ${borderSolid ?? "solid"} ${borderColor}` : "none",
        // , ${innerShadowDx}px ${innerShadowDy}px ${innerShadowBlur}px ${hexToRgba(
        //   innerShadowColor,
        //   innerShadowOpacity
        // )}`,
        // WebkitBackgroundClip: isInnerShadow ? "text" : undefined,
        // MozBackgroundClip: isInnerShadow ? "text" : undefined,
        // backgroundClip: isInnerShadow ? "text" : undefined,
      }}
    >
      <div
        className={`${cssPrefix}-label__inner tl-text-content__wrapper flex
        ${italic === "italic" ? "italic" : ""}
        ${underline === "underline" ? "underline" : ""}
        ${lineThrough === "line-through" ? "line-through" : ""}
        ${textJustify}
        `}
        style={{
          transform: `${flipH ? "scale(-1, 1)" : ""} ${flipV ? "scale(1, -1)" : ""}`,
          fontFamily: effectiveFontFamily,
          fontSize,
          fontWeight,
          lineHeight: fontSize * lineHeight + "px",
          minHeight: fontSize * lineHeight + "px",
          minWidth: textWidth,
          color: mixedColor,
          width: textWidth ? textWidth : undefined,
          height: textHeight ? textHeight : undefined,
          maxWidth: "100%",
          maxHeight: "100%",
          opacity: transparency,
          letterSpacing: letterSpacing + "px",
          WebkitTextStrokeColor: isHiddenBorder ? "transparent" : mixedOutlineColor,
          WebkitTextStrokeWidth: isHiddenBorder ? "0px" : `${strokeWidth}px`,
          filter: isHiddenLayerBlur ? undefined : `blur(${filterBlur}px)`,
          justifyContent: align === "middle" || legacyAlign ? "center" : align, // Horizontal alignment
          border: "none",
          outline: "none",
          pointerEvents: fixedHeight ? "none" : "auto", // Prevent pointer events from being captured in fixed size
        }}
      >
        <div
          style={{
            fontWeight,
            fontFamily: effectiveFontFamily,
          }}
          className={`${cssPrefix} tl-text tl-text-content ${textJustify}
          ${italic === "italic" ? "italic" : ""}
          ${underline === "underline" ? "underline" : ""}
          ${lineThrough === "line-through" ? "line-through" : ""}
          `}
          dir="auto"
        >
          {finalText.split("\n").map((lineOfText, index) => (
            <div
              className={`${textJustify}
            ${underline === "underline" ? "underline" : ""}
            ${lineThrough === "line-through" ? "line-through" : ""}
            ${italic === "italic" ? "italic" : ""}
            `}
              style={{
                fontFamily: effectiveFontFamily,
                fontWeight,
              }}
              key={index}
              dir="auto"
            >
              {lineOfText}
            </div>
          ))}
        </div>
        {(isEditingAnything || isSelected) && (
          <TextArea
            ref={rInput}
            // We need to add the initial value as the key here because we need this component to
            // 'reset' when this state changes and grab the latest defaultValue.
            key={initialText}
            text={text}
            isEditing={isEditing}
            {...editableTextRest}
            handleKeyDown={handleKeyDownCustom ?? editableTextRest.handleKeyDown}
            caretColor={mixedColor}
          />
        )}
      </div>
    </div>
  );
});
